import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  createOrGetChat, 
  sendMessage, 
  getMessages, 
  getUserChats, 
  markMessagesAsRead,
  listenMessages,
  subscribeToUserChats,
  getUnreadCount
} from '../services/messages.service';

// Hook for managing user chats
export const useUserChats = (userId) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadChats = useCallback(async () => {
    if (!userId) {
      console.log('No userId provided to useUserChats');
      setChats([]);
      setLoading(false);
      return;
    }
    
    console.log('Loading chats for user:', userId);
    setLoading(true);
    setError(null);
    
    try {
      const userChats = await getUserChats(userId);
      console.log('Loaded chats:', userChats.length);
      setChats(userChats);
    } catch (err) {
      console.error('Error loading chats:', err);
      setError('Failed to load chats');
      setChats([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!userId) {
      setChats([]);
      setLoading(false);
      return;
    }

    console.log('Setting up real-time subscription for user:', userId);
    setError(null);
    
    const unsubscribe = subscribeToUserChats(userId, (updatedChats) => {
      console.log('Real-time chat update:', updatedChats.length);
      setChats(updatedChats);
      setLoading(false);
      setError(null);
    });

    return () => {
      console.log('Cleaning up chat subscription');
      unsubscribe();
    };
  }, [userId]);

  return { chats, loading, error, refetch: loadChats };
};

// Hook for managing chat messages
export const useChatMessages = (chatId, userId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const loadMessages = useCallback(async () => {
    if (!chatId) {
      console.log('No chatId provided to useChatMessages');
      setMessages([]);
      setLoading(false);
      return;
    }
    
    console.log('Loading messages for chat:', chatId);
    setLoading(true);
    setError(null);
    
    try {
      const chatMessages = await getMessages(chatId);
      console.log('Loaded messages:', chatMessages.length);
      setMessages(chatMessages);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  const sendNewMessage = useCallback(async (messageText, messageType = 'text') => {
    if (!chatId || !userId || !messageText.trim()) {
      console.log('Cannot send message - missing required data');
      return;
    }
    
    console.log('Sending message:', messageText);
    setSending(true);
    try {
      await sendMessage(chatId, userId, messageText.trim(), messageType);
      console.log('Message sent successfully');
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  }, [chatId, userId]);

  const markAsRead = useCallback(async () => {
    if (!chatId || !userId) return;
    
    try {
      await markMessagesAsRead(chatId, userId);
      console.log('Messages marked as read');
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [chatId, userId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Subscribe to real-time message updates
  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    console.log('Setting up real-time message subscription for chat:', chatId);
    setLoading(true);
    setError(null);
    
    const unsubscribe = listenMessages(chatId, (updatedMessages) => {
      console.log('Real-time message update:', updatedMessages.length);
      setMessages(updatedMessages);
      setLoading(false);
      setError(null);
    });

    return () => {
      console.log('Cleaning up message subscription');
      unsubscribe();
    };
  }, [chatId]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (chatId && userId && messages.length > 0) {
      markAsRead();
    }
  }, [chatId, userId, messages.length, markAsRead]);

  return { 
    messages, 
    loading, 
    error, 
    sending, 
    sendMessage: sendNewMessage, 
    markAsRead,
    refetch: loadMessages 
  };
};

// Hook for creating new chats
export const useCreateChat = () => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const createChat = useCallback(async (user1Id, user2Id, propertyId = null) => {
    if (!user1Id || !user2Id) {
      console.log('Cannot create chat - missing user IDs');
      setError('Missing user information');
      return null;
    }
    
    console.log('Creating chat between:', user1Id, user2Id);
    setCreating(true);
    setError(null);
    
    try {
      const chat = await createOrGetChat(user1Id, user2Id, propertyId);
      console.log('Chat created successfully:', chat.id);
      return chat;
    } catch (err) {
      console.error('Error creating chat:', err);
      setError('Failed to create chat');
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return { createChat, creating, error };
};

// Hook for unread message count — real-time via onSnapshot on chat documents.
// A chat counts as "unread" when the last message was NOT sent by this user
// AND the user has not read it yet (based on lastReadAtByUser timestamp).
export const useUnreadCount = (userId) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const chatsQuery = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId)
    );

    const unsubChats = onSnapshot(chatsQuery, (chatsSnapshot) => {
      let totalUnread = 0;

      chatsSnapshot.docs.forEach((chatDoc) => {
        const data = chatDoc.data();

        // Skip deleted / hidden chats
        if ((data.deletedFor || []).includes(userId)) return;
        if ((data.hiddenForUsers || {})[userId] === true) return;

        // Skip chats with no messages
        if (!data.lastMessage && !data.lastMessageText && !data.lastMessageAt) return;

        // If I sent the last message, this chat is not unread for me
        const lastSender = data.lastSenderId || data.lastSenderID || data.lastMessageSenderId || '';
        if (lastSender === userId) return;

        // No lastMessageAt means we can't determine — skip
        const rawLMA = data.lastMessageAt;
        if (!rawLMA) return;

        const toMs = (v) => {
          if (!v) return 0;
          if (typeof v.toMillis === 'function') return v.toMillis();
          if (typeof v.toDate === 'function') return v.toDate().getTime();
          if (typeof v.seconds === 'number') return v.seconds * 1000;
          if (v instanceof Date) return v.getTime();
          if (typeof v === 'number') return v;
          const n = new Date(v).getTime();
          return isNaN(n) ? 0 : n;
        };

        const readByMap = data.lastReadAtBy || {};
        const readMs = toMs(readByMap[userId]);

        const lmaMs = toMs(rawLMA);
        if (!lmaMs) return;

        if (!readMs || lmaMs > readMs) {
          totalUnread += 1;
        }
      });

      setUnreadCount(totalUnread);
      setLoading(false);
    }, () => {
      setUnreadCount(0);
      setLoading(false);
    });

    return () => unsubChats();
  }, [userId]);

  const refetch = useCallback(() => {}, []);

  return { unreadCount, loading, refetch };
};

// Hook for agency unread message count — queries chats by agencyId (not participants)
export const useAgencyUnreadCount = (agencyId) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agencyId) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Agency chats are stored with agencyId field, not in participants array
    const chatsQuery = query(
      collection(db, 'chats'),
      where('agencyId', '==', agencyId)
    );

    const messageListeners = new Map();
    const chatUnreadCounts = new Map();

    const updateTotal = () => {
      let total = 0;
      chatUnreadCounts.forEach(count => { total += count; });
      setUnreadCount(total);
    };

    const unsubChats = onSnapshot(chatsQuery, (chatsSnapshot) => {
      const currentChatIds = new Set(chatsSnapshot.docs.map(d => d.id));

      // Remove listeners for chats that no longer exist
      messageListeners.forEach((unsub, chatId) => {
        if (!currentChatIds.has(chatId)) {
          unsub();
          messageListeners.delete(chatId);
          chatUnreadCounts.delete(chatId);
        }
      });

      // Add listeners for new chats
      chatsSnapshot.docs.forEach((chatDoc) => {
        if (messageListeners.has(chatDoc.id)) return;

        const messagesRef = collection(db, 'chats', chatDoc.id, 'messages');
        const unsubMessages = onSnapshot(messagesRef, (messagesSnapshot) => {
          const unread = messagesSnapshot.docs.filter(msgDoc => {
            const data = msgDoc.data();
            return data.senderId !== agencyId && data.read !== true;
          }).length;
          chatUnreadCounts.set(chatDoc.id, unread);
          updateTotal();
        }, () => {
          chatUnreadCounts.set(chatDoc.id, 0);
          updateTotal();
        });

        messageListeners.set(chatDoc.id, unsubMessages);
      });

      setLoading(false);
      updateTotal();
    }, () => {
      setUnreadCount(0);
      setLoading(false);
    });

    return () => {
      unsubChats();
      messageListeners.forEach(unsub => unsub());
      messageListeners.clear();
      chatUnreadCounts.clear();
    };
  }, [agencyId]);

  const refetch = useCallback(() => {
    // No-op: real-time listeners handle this automatically
  }, []);

  return { unreadCount, loading, refetch };
};

// Hook for message utilities
export const useMessageUtils = () => {
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Nu';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}u`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
      
      return date.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  }, []);

  const formatMessageTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting message time:', error);
      return '';
    }
  }, []);

  const getOtherParticipant = useCallback((chat, currentUserId) => {
    if (!chat || !chat.participants || !currentUserId) return null;
    
    const otherId = chat.participants.find(id => id !== currentUserId);
    if (!otherId) return null;
    
    return {
      id: otherId,
      ...chat.otherParticipant
    };
  }, []);

  return { formatTime, formatMessageTime, getOtherParticipant };
};